import React, { useEffect, useState, useRef } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { fmtAED } from '../lib/format';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Plus, Trash2, Pencil, Upload, Download, Star, ImagePlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import PageHelp from '../components/PageHelp';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absImg = (u) => (u && u.startsWith('/api/') ? `${BACKEND}${u}` : u);

const ALLERGENS = [['G', 'Gluten'], ['D', 'Dairy'], ['E', 'Eggs'], ['F', 'Fish'], ['N', 'Nuts'], ['S', 'Soy'], ['Y', 'Sulphites']];
const APP_CHANNELS = [['talabat', 'Talabat'], ['deliveroo', 'Deliveroo'], ['careem', 'Careem'], ['noonfood', 'Noon Food'], ['keeta', 'Keeta'], ['smiles', 'Smiles']];

export default function Menu() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [menus, setMenus] = useState([]);
  const [activeMenu, setActiveMenu] = useState(null);
  const [printers, setPrinters] = useState([]);
  const [brands, setBrands] = useState([]);
  const [tab, setTab] = useState('all');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [catOpen, setCatOpen] = useState(false);
  const [catForm, setCatForm] = useState({ name: '', color: '#0ea5e9' });
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuName, setMenuName] = useState('');
  const [copyFrom, setCopyFrom] = useState('');
  const [copyTarget, setCopyTarget] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importReplace, setImportReplace] = useState(true);
  const [importTarget, setImportTarget] = useState('');
  const [importing, setImporting] = useState(false);

  const load = async () => {
    const [mi, c, mn] = await Promise.all([api.get('/menu-items'), api.get('/categories'), api.get('/menus')]);
    setItems(mi.data); setCats(c.data); setMenus(mn.data);
    setActiveMenu((prev) => prev || mn.data.find((m) => m.is_active) || mn.data[0] || null);
    try { const ing = await api.get('/ingredients'); setIngredients(ing.data); } catch (e) {}
    try { const cfg = await api.get('/config'); setPrinters(cfg.data?.business?.printers || []); setBrands(cfg.data?.business?.brands || []); } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const menuItems = activeMenu ? items.filter((i) => i.menu_id === activeMenu.id) : items;
  const filtered = tab === 'all' ? menuItems : menuItems.filter((i) => i.category_id === tab);

  const openNew = () => { setEditing(null); setForm({ name: '', price: 0, price_delivery: '', price_agent: '', pricelist_prices: {}, veg: true, available: true, category_id: cats[0]?.id, station: 'Hot Kitchen', printer: printers[0] || '', description: '', tax_exempt: false, image: '', variants: [], modifiers: [], recipe: [] }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it, price_delivery: it.price_delivery ?? '', price_agent: it.price_agent ?? '', pricelist_prices: it.pricelist_prices || {}, recipe: it.recipe || [] }); setOpen(true); };

  const addRecipeRow = () => setForm((f) => ({ ...f, recipe: [...(f.recipe || []), { ingredient_id: ingredients[0]?.id || '', qty: 1 }] }));
  const updateRecipeRow = (i, field, val) => setForm((f) => ({ ...f, recipe: f.recipe.map((r, j) => j === i ? { ...r, [field]: val } : r) }));
  const removeRecipeRow = (i) => setForm((f) => ({ ...f, recipe: f.recipe.filter((_, j) => j !== i) }));

  const pickPhoto = () => fileRef.current?.click();
  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({ ...f, image: data.url }));
      toast.success('Photo uploaded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const save = async () => {
    const cat = cats.find((c) => c.id === form.category_id);
    const appNum = (k) => form[k] === '' || form[k] == null ? null : Number(form[k]);
    const payload = {
      ...form, price: Number(form.price), category_name: cat?.name || '',
      menu_id: form.menu_id || activeMenu?.id || null,
      code: (form.code || '').trim() || null,
      brand: form.brand || 'RoboChef',
      allergens: form.allergens || [],
      price_delivery: appNum('price_delivery'),
      price_agent: appNum('price_agent'),
      price_talabat: appNum('price_talabat'), price_deliveroo: appNum('price_deliveroo'),
      price_careem: appNum('price_careem'), price_noonfood: appNum('price_noonfood'),
      price_keeta: appNum('price_keeta'), price_smiles: appNum('price_smiles'),
      pricelist_prices: Object.fromEntries(Object.entries(form.pricelist_prices || {})
        .filter(([, v]) => v !== '' && v != null).map(([k, v]) => [k, Number(v)])),
      variants: (form.variants || []).filter((v) => v.name?.trim()).map((v) => ({
        name: v.name.trim(), price: Number(v.price) || 0,
        price_delivery: v.price_delivery === '' || v.price_delivery == null ? null : Number(v.price_delivery),
        price_agent: v.price_agent === '' || v.price_agent == null ? null : Number(v.price_agent),
      })),
      recipe: (form.recipe || []).filter((r) => r.ingredient_id).map((r) => ({ ingredient_id: r.ingredient_id, qty: Number(r.qty) || 0 })),
    };
    try {
      if (editing) await api.put(`/menu-items/${editing.id}`, payload);
      else await api.post('/menu-items', payload);
      setOpen(false); load(); toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const toggle = async (it) => { await api.patch(`/menu-items/${it.id}/availability`, null, { params: { available: !it.available } }); load(); };
  const del = async (it) => { await api.delete(`/menu-items/${it.id}`); load(); toast.success('Deleted'); };
  const addCat = async () => { await api.post('/categories', { ...catForm, sort: cats.length }); setCatOpen(false); setCatForm({ name: '', color: '#0ea5e9' }); load(); toast.success('Category added'); };

  const addMenu = async () => {
    if (!menuName.trim()) { toast.error('Enter a menu name'); return; }
    const { data } = await api.post('/menus', { name: menuName.trim(), is_active: false });
    if (copyFrom) {
      const res = await api.post(`/menus/${data.id}/copy-items`, { menu_id: copyFrom });
      toast.success(`Menu created · ${res.data.copied} items copied (edit their prices/variants freely)`);
    } else {
      toast.success('Menu created');
    }
    setMenuOpen(false); setMenuName(''); setCopyFrom(''); await load(); setActiveMenu(data);
  };

  const duplicateTo = async () => {
    if (!editing || !copyTarget) return;
    try {
      await api.post(`/menu-items/${editing.id}/copy`, { menu_id: copyTarget });
      toast.success(`"${editing.name}" copied to ${menus.find((m) => m.id === copyTarget)?.name} — edit its variant prices there`);
      setCopyTarget('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Copy failed'); }
  };
  const activateMenu = async (m) => { await api.post(`/menus/${m.id}/activate`); await load(); toast.success(`${m.name} is now the live POS menu`); };
  const delMenu = async (m) => {
    if (!window.confirm(`Delete menu "${m.name}"?`)) return;
    try { await api.delete(`/menus/${m.id}`); const rest = menus.filter((x) => x.id !== m.id); setActiveMenu(rest[0] || null); load(); toast.success('Menu deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const doImport = async () => {
    if (!importFile) { toast.error('Choose a file'); return; }
    setImporting(true);
    try {
      if (importFile.name.toLowerCase().endsWith('.json')) {
        const payload = JSON.parse(await importFile.text());
        const { data } = await api.post(`/menu/import-json?replace=${importReplace}`, { data: payload });
        toast.success(`Imported ${data.items} items, ${data.categories} categories, ${data.menus} menu set(s) (photos included)`);
      } else {
        const fd = new FormData();
        fd.append('file', importFile);
        const qs = `replace=${importReplace}${importTarget ? `&menu_id=${importTarget}` : ''}`;
        const { data } = await api.post(`/menu/import?${qs}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success(`Imported ${data.items_created} items${data.menus_created ? ` · ${data.menus_created} pricelist(s) created` : ''}`);
      }
      setImportOpen(false); setImportFile(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };

  const downloadTemplate = () => {
    const rows = [
      ['Category', 'Item', 'Description', 'Price', 'Type', 'Station', 'Image', 'Menu'],
      ['Starters', 'Spring Rolls', 'Crispy veg rolls', '18.00', 'Veg', 'Hot Kitchen', 'https://example.com/spring-rolls.jpg', 'Main Menu'],
      ['Mains', 'Grilled Chicken', 'With rice', '42.00', 'Non-Veg', 'Grill', '', 'DWTC Event Menu'],
      ['Mains', 'Grilled Chicken', 'With rice', '48.00', 'Non-Veg', 'Grill', '', 'Main Menu'],
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'menu_import_template.csv'; a.click(); URL.revokeObjectURL(url);
  };

  const exportMenu = async () => {
    const { data } = await api.get('/menu/export');
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
    const a = document.createElement('a'); a.href = url; a.download = 'robochef-menu-backup.json'; a.click(); URL.revokeObjectURL(url);
    toast.success('Menu exported — import this file on your other environment');
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <PageHelp id="menu">Create multiple <b>menu sets</b> (e.g. Main, Ramadan). Set prices per channel — <b>Dining</b>, <b>Delivery</b> and <b>Delivery Agent</b>. Upload item <b>photos</b>, assign a <b>printer</b>, and use the in-stock switch to instantly <b>'86'</b> an item.</PageHelp>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div><h1 className="font-display text-2xl font-bold">Menu Management</h1><p className="text-sm text-muted-foreground">{menuItems.length} items · {cats.length} categories</p></div>
        {can('menu') && <div className="flex gap-2"><Button variant="outline" data-testid="export-menu-button" onClick={exportMenu}><Download className="h-4 w-4 mr-1" /> Export</Button><Button variant="outline" data-testid="import-menu-button" onClick={() => { setImportTarget(activeMenu?.id || ''); setImportOpen(true); }}><Upload className="h-4 w-4 mr-1" /> Import</Button><Button variant="outline" data-testid="add-category-button" onClick={() => setCatOpen(true)}><Plus className="h-4 w-4 mr-1" /> Category</Button><Button data-testid="add-item-button" onClick={openNew}><Plus className="h-4 w-4 mr-1" /> Item</Button></div>}
      </div>

      {/* Menu sets */}
      <Card className="p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-muted-foreground mr-1">MENUS:</span>
          {menus.map((m) => (
            <div key={m.id} data-testid={`menu-tab-${m.name}`} className={`flex items-center gap-1 rounded-full border pl-3 pr-1 py-1 text-sm ${activeMenu?.id === m.id ? 'bg-primary text-primary-foreground border-primary' : 'bg-card'}`}>
              <button onClick={() => { setActiveMenu(m); setTab('all'); }} className="flex items-center gap-1">
                {m.is_active && <Star className="h-3 w-3 fill-current" />} {m.name}
              </button>
              {can('menu') && !m.is_active && <button title="Make live on POS" data-testid={`menu-activate-${m.name}`} onClick={() => activateMenu(m)} className="opacity-70 hover:opacity-100 px-1"><Star className="h-3.5 w-3.5" /></button>}
              {can('menu') && menus.length > 1 && <button data-testid={`menu-delete-${m.name}`} onClick={() => delMenu(m)} className="opacity-60 hover:opacity-100 px-1"><Trash2 className="h-3.5 w-3.5" /></button>}
            </div>
          ))}
          {can('menu') && <Button size="sm" variant="outline" className="h-8 rounded-full" data-testid="add-menu-button" onClick={() => setMenuOpen(true)}><Plus className="h-3.5 w-3.5 mr-1" /> Menu</Button>}
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">The menu with a ★ is the one shown live on the POS. Prices can differ per menu and per channel.</p>
      </Card>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
        <button onClick={() => setTab('all')} className={`px-4 py-2 rounded-xl text-sm border whitespace-nowrap ${tab === 'all' ? 'bg-primary text-primary-foreground' : 'bg-card'}`}>All</button>
        {cats.map((c) => <button key={c.id} onClick={() => setTab(c.id)} className={`px-4 py-2 rounded-xl text-sm border whitespace-nowrap ${tab === c.id ? 'bg-primary text-primary-foreground' : 'bg-card'}`}>{c.name}</button>)}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((it) => (
          <Card key={it.id} className="p-4 overflow-hidden" data-testid={`menu-row-${it.name}`}>
            {it.image && <img src={absImg(it.image)} alt={it.name} className="w-full h-28 object-cover rounded-lg mb-3 -mt-1" loading="lazy" />}
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-2">
                <span className={`h-3 w-3 rounded-full mt-1 ${it.veg ? 'bg-success' : 'bg-destructive'}`} />
                <div>
                  <div className="font-medium">{it.name}</div>
                  <div className="text-xs text-muted-foreground">{it.category_name} · {it.station}{it.printer ? ` · 🖨 ${it.printer}` : ''}</div>
                  <div className="font-mono text-sm mt-1">{fmtAED(it.price)}</div>
                  {(it.price_delivery != null || it.price_agent != null) && (
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {it.price_delivery != null && <span>Delivery {fmtAED(it.price_delivery)}</span>}
                      {it.price_agent != null && <span>{it.price_delivery != null ? ' · ' : ''}Agent {fmtAED(it.price_agent)}</span>}
                    </div>
                  )}
                </div>
              </div>
              <Badge variant={it.available ? 'secondary' : 'outline'} className={it.available ? '' : 'text-destructive'}>{it.available ? 'Available' : '86\'d'}</Badge>
            </div>
            {can('menu') && (
              <div className="flex items-center justify-between mt-3 pt-3 border-t">
                <div className="flex items-center gap-2 text-xs"><Switch checked={it.available} onCheckedChange={() => toggle(it)} data-testid={`toggle-avail-${it.name}`} /> In stock</div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`edit-item-${it.name}`} onClick={() => openEdit(it)}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => del(it)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Item{activeMenu ? ` · ${activeMenu.name}` : ''}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {/* Photo */}
            <div>
              <Label>Item Photo</Label>
              <div className="flex items-center gap-3 mt-1">
                <div className="h-20 w-20 rounded-lg border bg-muted overflow-hidden flex items-center justify-center shrink-0">
                  {form.image ? <img src={absImg(form.image)} alt="preview" className="h-full w-full object-cover" data-testid="item-photo-preview" /> : <ImagePlus className="h-6 w-6 text-muted-foreground" />}
                </div>
                <div className="flex flex-col gap-1">
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="item-photo-input" onChange={onPhoto} />
                  <Button type="button" size="sm" variant="outline" data-testid="item-photo-upload" disabled={uploading} onClick={pickPhoto}>{uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ImagePlus className="h-4 w-4 mr-1" />}{form.image ? 'Replace photo' : 'Upload photo'}</Button>
                  {form.image && <Button type="button" size="sm" variant="ghost" className="text-destructive h-7" onClick={() => setForm({ ...form, image: '' })}>Remove</Button>}
                </div>
              </div>
            </div>
            <div><Label>Name</Label><Input data-testid="item-name-input" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Item Code <span className="text-[11px] text-muted-foreground font-normal">(F&B master, e.g. RN-01)</span></Label><Input data-testid="item-code" placeholder="RN-01" value={form.code || ''} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} /></div>
              <div><Label>Brand</Label>
                <select className="w-full h-9 rounded-md border bg-background px-2" data-testid="item-brand" value={form.brand || 'RoboChef'} onChange={(e) => setForm({ ...form, brand: e.target.value })}>
                  {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Allergens <span className="text-[11px] text-muted-foreground font-normal">(master codes)</span></Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {ALLERGENS.map(([code, label]) => {
                  const on = (form.allergens || []).includes(code);
                  return (
                    <button key={code} type="button" data-testid={`item-allergen-${code}`}
                      onClick={() => setForm({ ...form, allergens: on ? (form.allergens || []).filter((a) => a !== code) : [...(form.allergens || []), code] })}
                      className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${on ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}>
                      {code} · {label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Dining Price</Label><Input data-testid="item-price-input" type="number" value={form.price || ''} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
              <div><Label>Delivery Price</Label><Input data-testid="item-price-delivery" type="number" placeholder="same" value={form.price_delivery ?? ''} onChange={(e) => setForm({ ...form, price_delivery: e.target.value })} /></div>
              <div><Label>Agent Price</Label><Input data-testid="item-price-agent" type="number" placeholder="same" value={form.price_agent ?? ''} onChange={(e) => setForm({ ...form, price_agent: e.target.value })} /></div>
            </div>
            <div className="border rounded-lg p-2.5 space-y-2">
              <Label className="text-xs">Delivery App Prices <span className="text-muted-foreground font-normal">(blank = Delivery price)</span></Label>
              <div className="grid grid-cols-3 gap-2">
                {APP_CHANNELS.map(([key, label]) => (
                  <div key={key}>
                    <Label className="text-[11px] text-muted-foreground">{label}</Label>
                    <Input type="number" className="h-8" data-testid={`item-price-${key}`} placeholder="—"
                      value={form[`price_${key}`] ?? ''} onChange={(e) => setForm({ ...form, [`price_${key}`]: e.target.value })} />
                  </div>
                ))}
              </div>
            </div>
            {menus.filter((m) => m.id !== (form.menu_id || activeMenu?.id)).length > 0 && (
              <div className="border rounded-lg p-2.5 space-y-2">
                <Label className="text-xs">Pricelist Prices <span className="text-muted-foreground font-normal">(blank = use Dining price)</span></Label>
                <div className="grid grid-cols-2 gap-2">
                  {menus.filter((m) => m.id !== (form.menu_id || activeMenu?.id)).map((m) => (
                    <div key={m.id}>
                      <Label className="text-[11px] text-muted-foreground">{m.name}</Label>
                      <Input type="number" className="h-8" data-testid={`item-pricelist-price-${m.name}`} placeholder={String(form.price || 'same')}
                        value={(form.pricelist_prices || {})[m.id] ?? ''}
                        onChange={(e) => setForm({ ...form, pricelist_prices: { ...(form.pricelist_prices || {}), [m.id]: e.target.value } })} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Category</Label><select className="w-full h-9 rounded-md border bg-background px-2" value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
              <div><Label>Station</Label><select className="w-full h-9 rounded-md border bg-background px-2" value={form.station} onChange={(e) => setForm({ ...form, station: e.target.value })}>{['Hot Kitchen', 'Grill', 'Cold Kitchen', 'Bar', 'Dessert'].map((s) => <option key={s}>{s}</option>)}</select></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Printer Assignment</Label>
                <select className="w-full h-9 rounded-md border bg-background px-2" data-testid="item-printer" value={form.printer || ''} onChange={(e) => setForm({ ...form, printer: e.target.value })}>
                  <option value="">— No printer —</option>
                  {printers.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                {printers.length === 0 && <p className="text-[11px] text-muted-foreground mt-1">Add printers in Settings → Preferences.</p>}
              </div>
              <div className="flex items-end gap-4 pb-1">
                <div className="flex items-center gap-2 text-sm"><Switch checked={form.veg} onCheckedChange={(v) => setForm({ ...form, veg: v })} /> Veg</div>
                <div className="flex items-center gap-2 text-sm"><Switch checked={form.tax_exempt} onCheckedChange={(v) => setForm({ ...form, tax_exempt: v })} /> Tax-exempt</div>
              </div>
            </div>
            <div><Label>Description</Label><Textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-1">
                <Label>Variants <span className="text-xs text-muted-foreground font-normal">(Small/Large, Half/Full — each priced; leave Delivery/Agent blank to use the variant price)</span></Label>
                <Button type="button" size="sm" variant="outline" data-testid="variant-add-row" onClick={() => setForm({ ...form, variants: [...(form.variants || []), { name: '', price: Number(form.price) || 0, price_delivery: null, price_agent: null }] })}><Plus className="h-3 w-3 mr-1" />Add</Button>
              </div>
              <div className="space-y-2">
                {(form.variants || []).map((v, i) => (
                  <div key={i} className="space-y-1 rounded-lg border p-2" data-testid={`variant-row-${i}`}>
                    <div className="flex gap-2 items-center">
                      <Input className="flex-1" placeholder="Variant (e.g. Small)" value={v.name} data-testid={`variant-name-${i}`} onChange={(e) => setForm({ ...form, variants: form.variants.map((x, j) => j === i ? { ...x, name: e.target.value } : x) })} />
                      <Input className="w-24" type="number" placeholder="Dining" title="Dining price" value={v.price} data-testid={`variant-price-${i}`} onChange={(e) => setForm({ ...form, variants: form.variants.map((x, j) => j === i ? { ...x, price: Number(e.target.value) } : x) })} />
                      <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => setForm({ ...form, variants: form.variants.filter((_, j) => j !== i) })}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                    <div className="flex gap-2 items-center">
                      <Input className="h-8 text-xs flex-1" type="number" placeholder="Delivery price (blank = same)" data-testid={`variant-price-delivery-${i}`} value={v.price_delivery ?? ''} onChange={(e) => setForm({ ...form, variants: form.variants.map((x, j) => j === i ? { ...x, price_delivery: e.target.value } : x) })} />
                      <Input className="h-8 text-xs flex-1" type="number" placeholder="Agent price (blank = same)" data-testid={`variant-price-agent-${i}`} value={v.price_agent ?? ''} onChange={(e) => setForm({ ...form, variants: form.variants.map((x, j) => j === i ? { ...x, price_agent: e.target.value } : x) })} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-1">
                <Label>Recipe / Ingredients <span className="text-xs text-muted-foreground font-normal">(auto-deducts stock on sale)</span></Label>
                <Button type="button" size="sm" variant="outline" data-testid="recipe-add-row" onClick={addRecipeRow} disabled={!ingredients.length}><Plus className="h-3 w-3 mr-1" />Add</Button>
              </div>
              {ingredients.length === 0 && <p className="text-xs text-muted-foreground">Add ingredients in the Inventory page first to build recipes.</p>}
              <div className="space-y-2">
                {(form.recipe || []).map((r, i) => {
                  const ing = ingredients.find((x) => x.id === r.ingredient_id);
                  return (
                    <div key={i} className="flex gap-2 items-center" data-testid={`recipe-row-${i}`}>
                      <select className="h-9 rounded-md border bg-background px-2 text-sm flex-1" value={r.ingredient_id} onChange={(e) => updateRecipeRow(i, 'ingredient_id', e.target.value)}>
                        {ingredients.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.unit})</option>)}
                      </select>
                      <Input className="w-24" type="number" step="0.01" placeholder="Qty" value={r.qty} onChange={(e) => updateRecipeRow(i, 'qty', e.target.value)} data-testid={`recipe-qty-${i}`} />
                      <span className="text-xs text-muted-foreground w-8">{ing?.unit}</span>
                      <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => removeRecipeRow(i)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  );
                })}
              </div>
            </div>

            <Button data-testid="item-save-button" className="w-full" onClick={save}>Save Item</Button>

            {editing && menus.filter((m) => m.id !== (form.menu_id || activeMenu?.id)).length > 0 && (
              <div className="border-t pt-3 flex items-center gap-2">
                <Label className="text-xs shrink-0">Duplicate to pricelist:</Label>
                <select className="h-9 rounded-md border bg-background px-2 text-sm flex-1" data-testid="item-copy-target"
                  value={copyTarget} onChange={(e) => setCopyTarget(e.target.value)}>
                  <option value="">Choose pricelist…</option>
                  {menus.filter((m) => m.id !== (form.menu_id || activeMenu?.id)).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <Button type="button" size="sm" variant="outline" data-testid="item-copy-button" disabled={!copyTarget} onClick={duplicateTo}>Copy</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Import Menu / Pricelist</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Upload a <b>CSV</b> or <b>Excel (.xlsx)</b> with columns: Category, Item, Description, Price, Type, Station, Image — add an optional <b>Menu</b> column to import several pricelists at once — or a <b>JSON backup</b> (keeps photos, variants &amp; channel prices).</p>
            <div>
              <Label>Import into pricelist</Label>
              <select className="w-full h-9 rounded-md border bg-background px-2" data-testid="import-target-menu" value={importTarget} onChange={(e) => setImportTarget(e.target.value)}>
                <option value="">— Active menu (default) —</option>
                {menus.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <Button variant="outline" size="sm" data-testid="download-template-button" onClick={downloadTemplate}><Download className="h-4 w-4 mr-1" /> Download CSV template</Button>
            <div>
              <Label>File</Label>
              <Input data-testid="import-file-input" type="file" accept=".csv,.xlsx,.xls,.json" onChange={(e) => setImportFile(e.target.files?.[0] || null)} />
            </div>
            <div className="flex items-center gap-2 text-sm"><Switch checked={importReplace} onCheckedChange={setImportReplace} data-testid="import-replace-switch" /> Replace existing menu</div>
            {importReplace && <p className="text-xs text-destructive">This will delete your current menu items & categories before importing.</p>}
            <Button data-testid="import-submit-button" className="w-full" disabled={importing} onClick={doImport}>{importing ? 'Importing…' : 'Import Menu'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={menuOpen} onOpenChange={setMenuOpen}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle>New Menu (Pricelist)</DialogTitle></DialogHeader>
          <div><Label>Menu name</Label><Input data-testid="menu-name-input" placeholder="e.g. Ramadan Menu" value={menuName} onChange={(e) => setMenuName(e.target.value)} /></div>
          <div>
            <Label>Copy items from</Label>
            <select className="w-full h-9 rounded-md border bg-background px-2" data-testid="menu-copy-from" value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
              <option value="">— Start empty —</option>
              {menus.map((m) => <option key={m.id} value={m.id}>{m.name} ({items.filter((i) => i.menu_id === m.id).length} items)</option>)}
            </select>
            <p className="text-[11px] text-muted-foreground mt-1">Copied items keep their variants — you can then set different variant prices for this pricelist.</p>
          </div>
          <Button data-testid="menu-save-button" onClick={addMenu}>Create Menu</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle>New Category</DialogTitle></DialogHeader>
          <div><Label>Name</Label><Input data-testid="cat-name-input" value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} /></div>
          <div><Label>Color</Label><Input type="color" value={catForm.color} onChange={(e) => setCatForm({ ...catForm, color: e.target.value })} /></div>
          <Button data-testid="cat-save-button" onClick={addCat}>Add</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
